import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ReferralRouteParams } from './types/';

import { IState } from 'models/reducers/i-state.interface';

import IntroSection from 'routes/singleEvent/components/IntroSection/IntroSection';
import RewardDistributionHistorySection from 'routes/singleEvent/components/RewardDistributionHistorySection/RewardDistributionHistorySection';

import { AnalyticsParticipationCriteria } from './components/AnalyticsParticipationCriteria';
import { ParticipationButton } from './components/ParticipationButton';
import { SectionTitle } from './components/SectionTitle';
import { TasksList } from './components/TasksList';
import { TasksProgressBar } from './components/TasksProgressBar';
import AnimatedBackground from 'components/animatedBackground/AnimatedBackground';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';

import { useEventRegistration } from './hooks/useEventRegistration';
import { useEventTypeRedirect } from './hooks/useEventTypeRedirect';
import { useTaskBasedEvent } from './hooks/useTaskBasedEvent';
import { useHomePageNavigate } from 'hooks/useHomePageNavigate';
import { useScroll } from 'hooks/useScroll';

import WheelIcon from 'assets/icons/wheel.svg';

import { REFERRAL_CACHE_KEYS } from './constants';
import { getParticipationStatus } from './helpers/map-user-status.helper';

const Badge = ({ text }: { text: string }) => {
  return (
    <div className="tw-flex tw-h-[30px] tw-flex-1 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-[5px] tw-bg-[#20AA67] tw-px-[10px] tw-font-jost tw-text-[15px] tw-font-semibold tw-text-white lg:tw-flex-none">
      {text}
    </div>
  );
};

const TaskBasedRoute = () => {
  const { id } = useParams<ReferralRouteParams>();
  const scrollTo = useScroll('scrollbar');
  const {
    data,
    isLoading,
    userStatus,
    tasks,
    paginatedTasks,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    shouldShowPagination,
    goToPage,
    goToNextPage,
    goToPreviousPage,
  } = useTaskBasedEvent();
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const { navigateToHomePage } = useHomePageNavigate();
  const queryClient = useQueryClient();

  const { mutate: registerForEvent, isPending: isRegistering } = useEventRegistration({
    eventId: id!,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.REFERRAL_EVENT, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_METRICS, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_STATUS, id] });
      setRegistrationError(null);
    },
    onError: (message) => {
      setRegistrationError(message);
    },
  });

  const participationStatus = useMemo(() => {
    return getParticipationStatus(data?.status, registrationError, userStatus, walletAddress);
  }, [data?.status, registrationError, userStatus, walletAddress]);

  const onClickRegister = () => {
    registerForEvent();
  };

  const onClickJoinNow = () => {
    setTimeout(() => {
      scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  useEffect(() => {
    if (!data && !isLoading) {
      navigateToHomePage();
    }
  }, [data, isLoading, navigateToHomePage]);

  useEventTypeRedirect({
    eventId: id,
    eventType: data?.eventType,
    currentRoute: 'referral',
    isLoading,
  });

  const onClickTaskRegister = (taskId: number) => {
    console.log('register', taskId);
    onClickJoinNow();
  };

  const onClickTaskClaim = (taskId: number) => {
    console.log('claim', taskId);
    onClickJoinNow();
  };

  return (
    <Page
      className="tw-overflow-x-hidden tw-bg-[#1E1E1E] tw-text-center tw-font-jost"
      pageBackground="dark"
      requireTermsOfServiceConsents
      loading={isLoading}
    >
      <AnimatedBackground />
      <Scrollbars className="scrollbar" style={{ width: '100%', height: '100%' }}>
        <PageWrapper homeFooter={false} landingFooter variant="events">
          {/* Intro section */}
          {data && (
            <IntroSection
              name={data.name}
              tagline={data.tagline}
              iconUrl={data.iconUrl}
              bannerUrl={data.bannerUrl}
              endAt={data.endAt}
              stages={data.stages}
              variant="referral"
              eventInfo={{
                eventId: id!,
                walletAddress,
                userStatus,
              }}
              withoutGradient
            >
              <div className="tw-mt-[40px] tw-flex tw-flex-col tw-items-center tw-gap-[16px] ">
                <ParticipationButton
                  participationStatus={participationStatus}
                  isRegistering={isRegistering}
                  onClickRegister={onClickRegister}
                  registrationError={registrationError}
                  eligibilityReasons={userStatus?.eligibility?.reasons}
                  hasWallet={!!walletAddress}
                />
              </div>
            </IntroSection>
          )}

          {/* Wheel of fortune section */}
          <div className="tw-flex tw-flex-col tw-items-center tw-px-[20px] tw-py-[50px] lg:tw-px-0 lg:tw-pt-[50px]">
            <SectionTitle>Wheel of Fortune</SectionTitle>
            <h3 className="tw-mt-[8px] tw-max-w-[930px] tw-font-jost tw-text-[18px] tw-font-medium tw-text-white">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
              dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex
              ea commodo consequat.
            </h3>
            <Spacing height={40} />
            {/* <WheelOfFortune
              loading={false}
              prizeNumber={undefined}
              data={wheelData}
              onSpin={() => {}}
              onStopSpinning={() => {}}
              disabled={false}
              variant="dark"
            /> */}
            <div
              className="tw-flex tw-h-[46px] tw-items-center tw-gap-[10px] tw-rounded-[60px] tw-border-solid tw-border-[#20AA67] tw-px-[15px] tw-font-jost tw-text-[24px] tw-font-semibold tw-text-[#20AA67]"
              style={{
                backgroundColor: 'rgba(32, 170, 103, 0.2)',
                borderWidth: '1px',
              }}
            >
              <span>Available spins</span>
              <span className="tw-text-white">2</span>
              <img src={WheelIcon} alt="wheel" className="tw-h-[28px] tw-w-[28px]" />
            </div>
          </div>

          {/* Event Tasks section */}
          <div className={twMerge('tw-flex tw-flex-col tw-items-center tw-px-[28px] tw-font-jost')}>
            <SectionTitle>Event Tasks</SectionTitle>
            <h3 className="tw-mt-[8px] tw-max-w-[930px] tw-font-jost tw-text-[18px] tw-font-medium tw-text-white">
              Complete Event Tasks, earn extra rewards, and boost your progress during every event.
            </h3>

            <Spacing height={40} />

            {/* Progresss bar and tasks */}
            <div className="tw-w-full tw-max-w-[1260px]">
              <div className="tw-flex tw-flex-col tw-gap-[20px]">
                <TasksProgressBar progress={0} maxProgress={100} />
                <TasksProgressBar progress={50} maxProgress={100} />
                <TasksProgressBar progress={100} maxProgress={100} />
              </div>

              <Spacing height={40} />

              <TasksList
                tasks={tasks}
                paginatedTasks={paginatedTasks}
                currentPage={currentPage}
                totalPages={totalPages}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                shouldShowPagination={shouldShowPagination}
                goToPage={goToPage}
                goToNextPage={goToNextPage}
                goToPreviousPage={goToPreviousPage}
                onRegister={onClickTaskRegister}
                onClaim={onClickTaskClaim}
              />
            </div>
          </div>

          <Spacing height={50} />

          {/* Participation criteria section */}
          {data && data.participationCriteria.length > 0 && (
            <AnalyticsParticipationCriteria
              participationCriteria={data.participationCriteria}
              onClickJoinNow={onClickJoinNow}
              className="!tw-bg-transparent lg:tw-px-[20px]"
            />
          )}

          {/* Reward distribution history section */}
          <RewardDistributionHistorySection eventId={id} />
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default TaskBasedRoute;
